// HomePage.jsx

// Dependencies
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import FutureHoursReport from '@/components/data/hours/FutureHoursReport';
import PageHeading from '@/components/common/PageHeading';
import LoadingOverlay from '@/components/common/LoadingOverlay';

// Services
import { getAllocationsFuturesHoursReport, getProfessionals } from '@/services/schedulingService';

// Styling
import { homeCss } from '@digital_monorepo/styling';

const HomePage = () => {
  const [data, setData] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [error, setError] = useState(false);
  useEffect(() => {
    getProfessionals()
      .then(setDirectory)
      .catch(() => {});
  }, []);
  const photos = useMemo(() => new Map(directory.map(p => [p.ProfessionalID, p.PhotoKey])), [directory]);
  const reportData = useMemo(() => data.map(p => ({ ...p, PhotoKey: p.PhotoKey || photos.get(p.ProfessionalID) })), [data, photos]);
  const [weeksToDisplay, setWeeksToDisplay] = useState(8);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  const fetchReportData = useCallback(async weeks => {
    try {
      setIsLoading(true);
      setError(false);
      const futureReport = await getAllocationsFuturesHoursReport(false, false, weeks);
      setData(futureReport);
    } catch (error) {
      console.error('Error fetching unengaged professionals data:', error);
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReportData(weeksToDisplay);
  }, [weeksToDisplay, fetchReportData]);

  const handleWeeksChange = e => {
    setWeeksToDisplay(Number(e.target.value));
  };

  // A professional's status is one flag, not one per week, so applying it
  // here keeps every week's table in sync the moment it changes anywhere.
  const handleStatusChange = (professionalID, newStatus) => {
    setData(prev => prev.map(item => (item.ProfessionalID === professionalID ? { ...item, Status: newStatus } : item)));
  };

  // Notes are per professional too; without this, a later status/data update
  // would rebuild that professional's row from the stale fetched notes and
  // revert whatever was just saved (even though the save itself succeeded).
  const handleNotesChange = (professionalID, newNotes) => {
    setData(prev => prev.map(item => (item.ProfessionalID === professionalID ? { ...item, ProfessionalNotes: newNotes } : item)));
  };

  return (
    <div className={homeCss.pageContainer}>
      {isLoading && <LoadingOverlay />}
      <PageHeading title='Team availability'>
        <span className='workspace-count'>
          <span className='status-dot' />
          {new Set(data.map(p => p.ProfessionalID)).size} professionals with availability
        </span>
      </PageHeading>
      <div className={homeCss.container}>
        <div className={homeCss.mainContent}>
          <div className={homeCss.tableResponsive}>
            {error ? (
              <div className='workspace-empty' role='alert'>
                <h2>Availability could not be loaded</h2>
                <p>Please try again.</p>
                <button className='workspace-button' onClick={() => fetchReportData(weeksToDisplay)}>
                  Retry
                </button>
              </div>
            ) : (
              <FutureHoursReport
                data={reportData}
                weeksToDisplay={weeksToDisplay}
                onWeeksChange={handleWeeksChange}
                weeksLabel={t('HOMEPAGE_TABLE.WEEKS_TO_DISPLAY')}
                weekSelectorClass={homeCss.weekSelector}
                formLabelClass={homeCss.formLabel}
                formSelectClass={homeCss.formSelect}
                onStatusChange={handleStatusChange}
                onNotesChange={handleNotesChange}
              />
            )}
            {!isLoading && !error && !data.length && (
              <div className='workspace-empty'>
                <h2>No availability in this period</h2>
                <p>Try another planning window using the weeks selector above.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
